import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import {
  useChatPricesVisibilityUpdateMutationMutation,
  ChatPricesVisibilityUpdateInput,
} from '../../../generated';

interface UseUpdateChatPricesVisibilityConfig {
  onSuccess?: (displayPricesOverride?: boolean) => void;
  onError?: (error: Error) => void;
  /**
   * Suppress this hook's own success/error toasts and only invoke the
   * `onSuccess`/`onError` callbacks. Used by the create flow so a single upper
   * layer owns the user-facing notifications; defaults to `false` so the
   * standalone Edit-chat usage keeps its toasts.
   */
  silent?: boolean;
}

export const useUpdateChatPricesVisibility = (
  config: UseUpdateChatPricesVisibilityConfig = {}
) => {
  const { onSuccess, onError, silent = false } = config;
  const { addNotification } = useNotification();

  const [updateChatPricesVisibility, { loading: isUpdating }] =
    useChatPricesVisibilityUpdateMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const updatePricesVisibility = useCallback(
    async (input: ChatPricesVisibilityUpdateInput) => {
      try {
        const result = await updateChatPricesVisibility({
          variables: {
            input,
          },
        });

        // `errorPolicy: 'all'` returns transport/GraphQL errors on the result;
        // treat them as a failure the caller can retry.
        const graphQLErrors = result.errors;
        if (graphQLErrors && graphQLErrors.length > 0) {
          throw new Error(
            graphQLErrors[0]?.message ||
              'Failed to update chat prices visibility'
          );
        }

        const response = result.data?.chatPricesVisibilityUpdate;
        if (response?.__typename === 'ChatPricesVisibilitySuccess') {
          if (!silent) {
            addNotification({
              title: 'Success',
              message: 'Chat prices visibility updated successfully.',
              type: 'success',
            });
          }
          onSuccess?.(response.displayPricesOverride ?? undefined);
          return;
        }

        // Business-union failures (InputValidationFailure / AuthorizationFailure)
        // carry a message; propagate them so a partial save stays dirty (the
        // Settings all-or-nothing save relies on this throw). The create flow
        // runs `silent` and catches the throw in its own step runner.
        const message =
          response && 'message' in response ? response.message : null;
        throw new Error(message || 'Failed to update chat prices visibility');
      } catch (updateError: unknown) {
        const error =
          updateError instanceof Error
            ? updateError
            : new Error('Failed to update chat prices visibility');
        onError?.(error);
        if (!silent) {
          addNotification({
            title: 'Error',
            message: error.message,
            type: 'error',
          });
        }
        throw error;
      }
    },
    [updateChatPricesVisibility, addNotification, onSuccess, onError, silent]
  );

  return {
    updatePricesVisibility,
    isUpdating,
  };
};
